import { c as _c } from "react/compiler-runtime";
import { useSearchPageInteraction, useSearchPageInteractionStore } from "@/hooks/search";
import { useOptimizedClicked } from "@/hooks/search/use-optimized-clicked";
import { usePropertyInteractionStore } from "@/stores/property-interaction-store";
import { MapItemSUP } from "@/types/search";
import { cn } from "@/utils/cn";
import React from "react";
import { SUPCard } from "../../sup/card";
export interface Project {
  projectId?: string;
  name?: string;
  sellerProjects?: string[];
  units?: string[];
  handout?: boolean;
}
interface SearchItemProps {
  data: MapItemSUP;
  rank: number;
  disableHighlight?: boolean;
  disableOnClick?: boolean;
  hChecked?: ((props: {
    id: string;
  }) => void) | undefined;
  checked?: boolean;
  className?: string;
  isFirstInView?: boolean;
}

// Priority loading for first 4 items (2x2 grid on desktop, or first 4 on mobile) to improve LCP
const LCP_PRIORITY_THRESHOLD = 4;
export const SearchItem = React.memo(function SearchItem(t0) {
  const $ = _c(47);
  const {
    data,
    rank,
    disableHighlight,
    disableOnClick,
    className,
    isFirstInView: t1
  } = t0;
  const isFirstInView = t1 === undefined ? false : t1;
  const {
    gps,
    coordinates: c,
    mapCoordinates
  } = data as MapItemSUP & {
    mapCoordinates?: [number, number] | string;
  };
  let t2;
  bb0: {
    if (mapCoordinates) {
      if (Array.isArray(mapCoordinates)) {
        t2 = mapCoordinates;
        break bb0;
      }
      if (typeof mapCoordinates === "string") {
        let lat;
        let lng;
        let t3;
        if ($[0] !== mapCoordinates) {
          const [lngStr, latStr] = mapCoordinates.split(",").map(_temp);
          lng = Number.parseFloat(lngStr ?? "");
          lat = Number.parseFloat(latStr ?? "");
          t3 = Number.isFinite(lng) && Number.isFinite(lat);
          $[0] = mapCoordinates;
          $[1] = lat;
          $[2] = lng;
          $[3] = t3;
        } else {
          lat = $[1];
          lng = $[2];
          t3 = $[3];
        }
        if (t3) {
          let t4;
          if ($[4] !== lat || $[5] !== lng) {
            t4 = [lng, lat];
            $[4] = lat;
            $[5] = lng;
            $[6] = t4;
          } else {
            t4 = $[6];
          }
          t2 = t4;
          break bb0;
        }
      }
    }
    if (gps) {
      let t3;
      if ($[7] !== gps.lat || $[8] !== gps.lng) {
        t3 = [gps.lng, gps.lat];
        $[7] = gps.lat;
        $[8] = gps.lng;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      t2 = t3;
      break bb0;
    }
    t2 = c;
  }
  const coordinates = t2;
  let t3;
  bb1: {
    if (!coordinates) {
      t3 = "";
      break bb1;
    }
    if (Array.isArray(coordinates)) {
      let t4;
      if ($[10] !== coordinates) {
        t4 = coordinates.toString();
        $[10] = coordinates;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      t3 = t4;
      break bb1;
    }
    if (typeof coordinates === "string") {
      t3 = coordinates;
      break bb1;
    }
    t3 = "";
  }
  const coordinatesString = t3;
  const elementRef = React.useRef(null);
  const isHoveredRef = React.useRef(false);
  const isGloballyHoveredRef = React.useRef(false);
  const {
    isClicked,
    handleToggleClick
  } = useOptimizedClicked(coordinatesString);
  const hoverKey = `${data._id}-${coordinatesString}`;
  const interactionStore = useSearchPageInteractionStore();
  const setHoveredMarkerId = useSearchPageInteraction(_temp2);
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = isHovered => {
      if (elementRef.current) {
        const element = elementRef.current;
        if (isHovered) {
          element.classList.add("shadow-xl", "scale-[1.02]");
        } else {
          element.classList.remove("shadow-xl", "scale-[1.02]");
        }
      }
    };
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const applyHoverEffects = t4;
  let t5;
  if ($[13] !== hoverKey || $[14] !== setHoveredMarkerId) {
    t5 = () => {
      isHoveredRef.current = true;
      applyHoverEffects(true);
      setHoveredMarkerId(hoverKey);
    };
    $[13] = hoverKey;
    $[14] = setHoveredMarkerId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[16] !== setHoveredMarkerId) {
    t6 = () => {
      isHoveredRef.current = false;
      applyHoverEffects(false);
      setHoveredMarkerId(null);
    };
    $[16] = setHoveredMarkerId;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleMouseLeave = t6;
  let t7;
  let t8;
  if ($[18] !== hoverKey || $[19] !== interactionStore) {
    t7 = () => {
      const unsubscribe = interactionStore.subscribe(state_0 => {
        const isGloballyHovered = state_0.hoveredMarkerId === hoverKey;
        if (isGloballyHovered !== isGloballyHoveredRef.current) {
          isGloballyHoveredRef.current = isGloballyHovered;
          applyHoverEffects(isHoveredRef.current || isGloballyHoveredRef.current);
        }
      });
      const state_1 = interactionStore.getState();
      const isGloballyHovered_0 = state_1.hoveredMarkerId === hoverKey;
      if (isGloballyHovered_0 !== isGloballyHoveredRef.current) {
        isGloballyHoveredRef.current = isGloballyHovered_0;
        applyHoverEffects(isHoveredRef.current || isGloballyHoveredRef.current);
      }
      return unsubscribe;
    };
    t8 = [interactionStore, hoverKey, applyHoverEffects];
    $[18] = hoverKey;
    $[19] = interactionStore;
    $[20] = t7;
    $[21] = t8;
  } else {
    t7 = $[20];
    t8 = $[21];
  }
  React.useEffect(t7, t8);
  const isHighlighted = !disableHighlight && isClicked;
  let t9;
  bb2: {
    if (data.relatedSups && data.relatedSups.length > 0) {
      t9 = data.relatedSups;
      break bb2;
    }
    let t10;
    if ($[22] !== data) {
      t10 = [data];
      $[22] = data;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    t9 = t10;
  }
  const propertiesAtCoordinates = t9;
  const openPropertyOverlay = usePropertyInteractionStore(_temp3);
  let t10;
  if ($[24] !== coordinates || $[25] !== coordinatesString || $[26] !== data._id || $[27] !== data.projectId || $[28] !== data.projectName || $[29] !== disableOnClick || $[30] !== handleToggleClick || $[31] !== openPropertyOverlay || $[32] !== propertiesAtCoordinates) {
    t10 = e => {
      if (disableOnClick) {
        return;
      }
      e.stopPropagation();
      if (!coordinatesString || !coordinates) {
        return;
      }
      const initialSupId = data._id;
      openPropertyOverlay({
        sups: propertiesAtCoordinates,
        initialSupId,
        projectName: data.projectName || ""
      });
      handleToggleClick({
        coordinates,
        projectId: data.projectId || "",
        projectName: data.projectName || ""
      });
    };
    $[24] = coordinates;
    $[25] = coordinatesString;
    $[26] = data._id;
    $[27] = data.projectId;
    $[28] = data.projectName;
    $[29] = disableOnClick;
    $[30] = handleToggleClick;
    $[31] = openPropertyOverlay;
    $[32] = propertiesAtCoordinates;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const handleClick = t10;
  let t11;
  if ($[34] !== className) {
    t11 = cn("search-item-card", "flex w-full", "gap-0.5", "cursor-pointer", "transition-all duration-300", "bg-white", "rounded-2xl overflow-hidden", className);
    $[34] = className;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const t12 = `${isHighlighted ? "shadow-xl" : ""}`;
  const t13 = rank <= LCP_PRIORITY_THRESHOLD;
  let t14;
  if ($[36] !== data || $[37] !== isFirstInView || $[38] !== t12 || $[39] !== t13) {
    t14 = <SUPCard data={data} className={t12} shouldPrioritizeCoverImage={t13} videoAutoplayOnLoad={isFirstInView} />;
    $[36] = data;
    $[37] = isFirstInView;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== handleClick || $[42] !== handleMouseEnter || $[43] !== handleMouseLeave || $[44] !== t11 || $[45] !== t14) {
    t15 = <><div ref={elementRef} className={t11} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick}>{t14}</div></>;
    $[41] = handleClick;
    $[42] = handleMouseEnter;
    $[43] = handleMouseLeave;
    $[44] = t11;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
});
function _temp(s) {
  return s.trim();
}
function _temp2(state) {
  return state.setHoveredMarkerId;
}
function _temp3(state_2) {
  return state_2.openPropertyOverlay;
}